/**
 * Dashboard Tasks Filter - Inline controls (Search, Status, Date Range)
 * Matches image: Search top row, Status + Date Range inline on second row
 */
import React from 'react';
import { Box, TextField, InputAdornment, Select, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Controller, useFormContext } from 'react-hook-form';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import {
  defaultTasksFilterValues,
  DashboardTasksFilterName,
} from './DashboardFilterUtils';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: '1', label: 'New' },
  { value: '2', label: 'Pending' },
  { value: '3', label: 'In Progress' },
  { value: '4', label: 'Completed' },
  { value: '5', label: 'Cancelled' },
  { value: '6', label: 'On Hold' },
];

export const defaultValues = defaultTasksFilterValues;

export interface DashboardTasksFilterProps {
  onApplyFilter: (values: Record<string, unknown>) => void;
  onAfterReset?: () => void;
}

export const DashboardTasksFilter: React.FC<DashboardTasksFilterProps> = ({
  onApplyFilter,
  onAfterReset,
}) => {
  const { setValue, handleSubmit, watch } = useFormContext();
  const statusIds = watch(DashboardTasksFilterName.statusIds);
  const statusValue = statusIds && typeof statusIds === 'object' && 'value' in statusIds
    ? (statusIds as { value: string }).value
    : '';

  const resetFilter = () => {
    Object.entries(defaultTasksFilterValues).forEach(([key, val]) => setValue(key, val));
    onApplyFilter(defaultTasksFilterValues);
    onAfterReset?.();
  };

  const handleStatusChange = (value: string) => {
    const next = value
      ? { id: Number(value), name: STATUS_OPTIONS.find(o => o.value === value)?.label ?? '', value }
      : null;
    setValue(DashboardTasksFilterName.statusIds, next);
    onApplyFilter({ ...defaultTasksFilterValues, ...watch(), statusIds: next });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minWidth: 0 }}>
      {/* Row 1: Search */}
      <Controller
        name={DashboardTasksFilterName.search}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            placeholder="Search…"
            sx={{ width: '100%', maxWidth: 400, '& .MuiOutlinedInput-root': { height: 34, borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            onKeyDown={e => e.key === 'Enter' && handleSubmit(onApplyFilter)()}
          />
        )}
      />
      {/* Row 2: Status + Date Range (inline controls) */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Select
          size="small"
          displayEmpty
          value={statusValue}
          onChange={e => handleStatusChange(e.target.value as string)}
          sx={{ minWidth: 155, height: 34, borderRadius: 2, fontSize: '0.875rem' }}
        >
          <MenuItem value="">Status</MenuItem>
          {STATUS_OPTIONS.filter(o => o.value).map(o => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.875rem' }}>{o.label}</MenuItem>
          ))}
        </Select>
        <Controller
          name={DashboardTasksFilterName.dateRange}
          render={({ field: { onChange, value } }) => (
            <DateRangePicker
              initialDateRange={value ?? undefined}
              onApply={val => {
                const next = val?.startDate || val?.endDate ? val : null;
                onChange(next);
                onApplyFilter({ ...defaultTasksFilterValues, ...watch(), dateRange: next });
              }}
              placeholder="MM/DD/YYYY - MM/DD/YYYY"
              applyOnClickAway
              size="inline"
              textFieldSize="small"
              trailingIconShow
              variant="outlined"
            />
          )}
        />
      </Box>
    </Box>
  );
};
